import { useState } from 'react'
import { loadSavedViews } from '../../lib/outreach/api'
import type { SavedView } from '../../lib/outreach/savedViews'
import type { CustomFieldDef } from '../../lib/outreach/types'
import { setSetting } from '../../lib/settings'
import { toast } from '../../lib/toast'
import { Button } from '../../shared/ui'
import { filtersToView, viewToFilters } from './savedViewMapping'
import type { ContactsFilters } from './useContacts'

/**
 * Saved views for the contacts list: pick one to apply its filters, or save
 * the current filters under a name. Views can include a custom field filter.
 * Stored in the same `saved_views` setting the legacy page used.
 */

interface Props {
  filters: ContactsFilters
  customDefs: CustomFieldDef[]
  onApply: (next: ContactsFilters) => void
}

type PaywallHost = {
  huntsCanSaveAnotherView?: (count: number) => boolean
  showPaywall?: (key: string) => void
}

export function SavedViewsMenu({ filters, customDefs, onApply }: Props) {
  const [views, setViews] = useState<SavedView[]>(() => loadSavedViews())
  const [selected, setSelected] = useState('')
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')

  const persist = async (next: SavedView[]) => {
    await setSetting('saved_views', JSON.stringify(next))
    setViews(next)
  }

  const apply = (value: string) => {
    setSelected(value)
    const view = views.find((v) => v.name === value)
    if (view) onApply(viewToFilters(view, filters, customDefs))
  }

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const host = window as unknown as PaywallHost
    const replacing = views.some((v) => v.name === trimmed)
    if (!replacing && host.huntsCanSaveAnotherView && !host.huntsCanSaveAnotherView(views.length)) {
      host.showPaywall?.('saved-views')
      return
    }
    const next = [...views.filter((v) => v.name !== trimmed), filtersToView(trimmed, filters)]
    await persist(next)
    setSelected(trimmed)
    setNaming(false)
    setName('')
    toast('View saved', 'success')
  }

  const remove = async () => {
    await persist(views.filter((v) => v.name !== selected))
    setSelected('')
    toast('View deleted')
  }

  return (
    <>
      <select
        className="filter-select"
        id="c-views-sel"
        aria-label="Saved views"
        value={selected}
        onChange={(e) => apply(e.target.value)}
      >
        <option value="">Views…</option>
        {views.map((v) => (
          <option key={v.name} value={v.name}>
            {v.name}
          </option>
        ))}
      </select>
      {selected && (
        <Button variant="secondary" onClick={() => void remove()}>
          Delete view
        </Button>
      )}
      {naming ? (
        <>
          <input
            className="filter-select"
            aria-label="View name"
            placeholder="View name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save()
              if (e.key === 'Escape') setNaming(false)
            }}
            style={{ width: 160 }}
          />
          <Button onClick={() => void save()}>Save</Button>
          <Button variant="secondary" onClick={() => setNaming(false)}>
            Cancel
          </Button>
        </>
      ) : (
        <Button variant="secondary" onClick={() => setNaming(true)}>
          Save view
        </Button>
      )}
    </>
  )
}
